/**
 * @typedef MaterialidadModuleAccessFlags
 * @description Banderas de `useRolePermissions` que habilitan la entrada al
 * modulo Materialidad. Son exactamente las que protegen sus rutas internas en
 * `apps/host/src/materialidad/app/app.tsx`.
 */
export type MaterialidadModuleAccessFlags = {
  /** Accion `VerFacturas`: guard de `carga-facturas`. */
  canViewListInvoices: boolean;
  /** Accion `VerFacturasVisor`: guard de `carga-facturas` y de `carga-facturas/visor`. */
  canViewListAllInvoices: boolean;
  /** Seccion `ListadoFacturas`: guard de `carga-facturas` via `orPermissions`. */
  canViewInvoices: boolean;
  /** Seccion `FacturasVisor`: guard de `carga-facturas` y de `carga-facturas/visor`. */
  canViewInvoicesVisor: boolean;
  /** Actividad `VerRevisionMaterialidad`: guard de `por-revisar`. */
  canViewRevisionMaterialidad: boolean;
  /** Actividad `AtenderSolicitudBorrado` (o rol Empresa): pestaña "Solicitudes de borrado" de `por-revisar`. */
  canAttendDeleteRequest?: boolean;
};

/**
 * @function canOpenMaterialidadModule
 * @description Unica fuente de verdad para decidir si el modulo Materialidad
 * debe ofrecerse al usuario (tarjeta del dashboard, menu lateral y autocarga
 * del ultimo modulo visitado).
 *
 * La condicion replica el guard de la ruta de entrada `carga-facturas`, que es
 * un OR entre `requiredPermission`, `alternativePermission` y `orPermissions`:
 *
 * ```tsx
 * <PermissionRouteGuard
 *   requiredPermission="canViewListInvoices"
 *   alternativePermission="canViewListAllInvoices"
 *   orPermissions={['canViewInvoices', 'canViewInvoicesVisor']}
 * />
 * ```
 *
 * Además se admite a quien solo revisa (`canViewRevisionMaterialidad`, ruta
 * `por-revisar`): un auditor puede no tener ninguna sección de listado. Igual quien solo atiende
 * solicitudes de borrado (`canAttendDeleteRequest`, pestaña de la misma ruta).
 *
 * `detalle-factura` no entra en el calculo: se alcanza desde las pantallas
 * anteriores o desde un enlace de Power BI (`/facturas/:token`). Esa pagina
 * exige esta misma regla, junto con el permiso de detalle, antes de navegar.
 *
 * Se deriva de los mismos flags que protegen las rutas (y no de `ClavesMenu`
 * del login) para que el acceso ofrecido y el acceso realmente concedido no
 * puedan desincronizarse.
 *
 * @param {MaterialidadModuleAccessFlags} permisos Banderas de `useRolePermissions`.
 * @returns {boolean} `true` si el usuario puede abrir alguna pantalla del modulo.
 */
export const canOpenMaterialidadModule = (
  permisos: MaterialidadModuleAccessFlags
): boolean =>
  permisos.canViewListInvoices ||
  permisos.canViewListAllInvoices ||
  permisos.canViewInvoices ||
  permisos.canViewInvoicesVisor ||
  permisos.canViewRevisionMaterialidad ||
  Boolean(permisos.canAttendDeleteRequest);
